import { useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { ImagePlus, Lightbulb, Link as LinkIcon, Plus, X } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import PlatformGlyph from "@/app/routes/dashboard/components/PlatformGlyph";
import { queue } from "@/components/Toast";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import type { Brand } from "@/hooks/useGetBrands";
import { cn } from "@/utils/helpers";
import { postReferenceSubmission, submissionsKey, useOrgId } from "../api/useReferences";
import { ReferenceBrandSelect } from "./ReferenceBrandPicker";
import { useQueryClient } from "@tanstack/react-query";

/*
 * References / Save (library, style B): a 720-wide sheet, radius 16. A paste
 * field for links and ideas, one row per item with its own why, pictures and
 * videos dropped or picked from the computer, then FOR (the brand) and Save.
 * Past ten items the count turns red and a note says what happens to the rest.
 *
 * What is sent: the links and ideas go as one submission, each link on its own
 * line with its why beside it (the interpret pass pairs them); every file goes
 * as its own submission with its why as the words, so each picture keeps its
 * own. Saving closes the sheet and says Queued; a failure keeps everything.
 */

const MAX_ITEMS = 10;
const MAX_CHARS = 1000;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const URL_RE = /^https?:\/\/\S+$/i;

type Item =
  | { id: string; kind: "link"; url: string; why: string }
  | { id: string; kind: "idea"; text: string }
  | {
      id: string;
      kind: "file";
      file: File;
      preview: string;
      why: string;
      progress: number;
      s3Key: string | null;
      error: string | null;
    };

function platformOf(url: string): string | null {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (host.includes("tiktok")) return "TIKTOK";
    if (host.includes("instagram")) return "INSTAGRAM";
    if (host.includes("youtube") || host.includes("youtu.be")) return "YOUTUBE";
    if (host.includes("pinterest")) return "PINTEREST";
    return null;
  } catch {
    return null;
  }
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function putWithProgress(url: string, file: File, mimeType: string, onProgress: (p: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", mimeType);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
}

export default function ReferenceSaveSheet({
  isOpen,
  onClose,
  brands,
  defaultBrandId,
  allowEveryone,
}: {
  isOpen: boolean;
  onClose: () => void;
  brands: Brand[];
  defaultBrandId: string | null;
  allowEveryone: boolean;
}) {
  const portal = useCoShellPortal();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [items, setItems] = useState<Item[]>([]);
  const [draft, setDraft] = useState("");
  const [brandId, setBrandId] = useState<string | null>(
    defaultBrandId ?? (allowEveryone ? null : (brands[0]?.id ?? null)),
  );
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const patch = (id: string, next: Partial<Item>) =>
    setItems((list) => list.map((item) => (item.id === id ? ({ ...item, ...next } as Item) : item)));

  const addText = (text: string) => setItems((list) => [...list, ...parseText(text)]);

  function parseText(text: string): Item[] {
    const parts = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    const next: Item[] = [];
    for (const line of parts) {
      const tokens = line.split(/\s+/);
      const urls = tokens.filter((t) => URL_RE.test(t));
      if (urls.length === 0) {
        next.push({ id: crypto.randomUUID(), kind: "idea", text: line });
        continue;
      }
      const rest = tokens.filter((t) => !URL_RE.test(t)).join(" ").replace(/^[—–-]\s*/, "");
      urls.forEach((url, i) =>
        next.push({ id: crypto.randomUUID(), kind: "link", url, why: i === urls.length - 1 ? rest : "" }),
      );
    }
    return next;
  }

  const addFiles = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const mimeType = file.type;
      if (!IMAGE_TYPES.includes(mimeType) && !VIDEO_TYPES.includes(mimeType)) {
        queue.add({ title: `${file.name} is not a picture or video we can read`, variant: "error" });
        continue;
      }
      const id = crypto.randomUUID();
      const item: Item = { id, kind: "file", file, preview: URL.createObjectURL(file), why: "", progress: 0, s3Key: null, error: null };
      setItems((list) => [...list, item]);
      void (async () => {
        try {
          const res = await axiosInstance.post<{ uploadUrl: string; s3Key: string; mimeType: string }>(
            `/references/${orgId}/uploads`,
            { fileName: file.name, mimeType, sizeBytes: file.size },
          );
          await putWithProgress(res.data.uploadUrl, file, res.data.mimeType, (p) => patch(id, { progress: p }));
          patch(id, { s3Key: res.data.s3Key, progress: 100 });
        } catch (error) {
          patch(id, { error: getAiRequestErrorMessage(error, "Upload failed") });
        }
      })();
    }
  };

  const remove = (id: string) =>
    setItems((list) => {
      const gone = list.find((i) => i.id === id);
      if (gone?.kind === "file") URL.revokeObjectURL(gone.preview);
      return list.filter((i) => i.id !== id);
    });

  const pending = draft.trim() ? parseText(draft) : [];
  const textItems = [...items, ...pending].filter((i) => i.kind !== "file");
  const files = items.filter((i): i is Extract<Item, { kind: "file" }> => i.kind === "file");
  const uploading = files.some((f) => !f.s3Key && !f.error);
  const count = items.length + pending.length;
  const overCap = textItems.length > MAX_ITEMS;
  const input = textItems
    .map((i) => (i.kind === "link" ? (i.why.trim() ? `${i.url} — ${i.why.trim()}` : i.url) : i.text))
    .join("\n");
  const tooLong = input.length > MAX_CHARS;
  const brandMissing = !allowEveryone && !brandId;

  const reset = () => {
    files.forEach((f) => URL.revokeObjectURL(f.preview));
    setItems([]);
    setDraft("");
    setFailed(null);
  };

  const save = async () => {
    if (count === 0 || uploading || brandMissing || tooLong) return;
    setSaving(true);
    setFailed(null);
    try {
      if (input.trim()) await postReferenceSubmission(orgId, { input, brandId });
      for (const f of files.filter((f) => f.s3Key)) {
        await postReferenceSubmission(orgId, {
          input: f.why.trim(),
          brandId,
          uploads: [{ s3Key: f.s3Key!, fileName: f.file.name, mimeType: f.file.type }],
        });
      }
      void qc.invalidateQueries({ queryKey: submissionsKey(orgId) });
      queue.add({ title: "Queued", description: "It shows up on the wall once the analyzer is done with it." });
      reset();
      onClose();
    } catch (error) {
      setFailed(getAiRequestErrorMessage(error, "Please try again. Everything you added is still here."));
    } finally {
      setSaving(false);
    }
  };

  const title = count > 1 ? `Save ${count} references` : "Save a reference";

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable={!saving}
      UNSTABLE_portalContainer={portal ?? undefined}
      className={({ isEntering }) =>
        cn(
          "fixed inset-0 z-[var(--z-index-modal)] flex items-center justify-center bg-co-scrim p-4 max-sm:items-end max-sm:p-2",
          isEntering && "animate-in-smooth fade-in",
        )
      }
    >
      <Modal className={({ isEntering }) => cn("w-full max-w-[720px] outline-none", isEntering && "animate-in-smooth dialog-in max-sm:sheet-in")}>
        <Dialog aria-label="Save" className="outline-none">
         <div
          className={cn("ref-save", dragging && "is-dragging")}
          onDragOver={(e: DragEvent) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e: DragEvent) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
          }}
        >
          <div className="flex items-center justify-between px-6 pb-3 pt-[22px]">
            <Heading slot="title" className="ref-save-title">
              {title}
            </Heading>
            <button type="button" className="ref-icon-close" aria-label="Close" onClick={onClose}>
              <X aria-hidden className="size-3.5" />
            </button>
          </div>

          {/* pt-1: the first field's 2px focus ring sits outside it, and a scroller clips what overflows. */}
          <div className="custom-scrollbar flex max-h-[min(640px,calc(100dvh-220px))] flex-col gap-2.5 overflow-y-auto px-6 pb-5 pt-1">
            <label className="ref-paste co-glass-control">
              <LinkIcon aria-hidden className="size-3.5 shrink-0 text-co-faint" />
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
                  const text = e.clipboardData.getData("text");
                  if (e.clipboardData.files.length) {
                    e.preventDefault();
                    addFiles(e.clipboardData.files);
                  } else if (text.includes("\n") || URL_RE.test(text.trim())) {
                    e.preventDefault();
                    addText(text);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && draft.trim()) {
                    e.preventDefault();
                    addText(draft);
                    setDraft("");
                  }
                }}
                placeholder={items.length ? "Paste another link, or type an idea" : "Paste a link, or type an idea"}
                aria-label="Paste a link, or type an idea"
              />
            </label>

            {items.map((item) =>
              item.kind === "link" ? (
                <div key={item.id} className="ref-save-row">
                  <span className="ref-save-thumb grid place-items-center" aria-hidden>
                    {platformOf(item.url) ? (
                      <PlatformGlyph platform={platformOf(item.url)!} className="size-5 text-co-text2" />
                    ) : (
                      <LinkIcon className="size-[18px] text-co-text2" />
                    )}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="ref-mono-faint truncate">{hostOf(item.url)}</span>
                    <span className="truncate text-[13px] font-semibold leading-[1.5] text-co-text">{item.url}</span>
                    <textarea
                      rows={1}
                      value={item.why}
                      onChange={(e) => patch(item.id, { why: e.target.value })}
                      placeholder="Why is it here? Say what to take from it."
                      aria-label={`Why ${hostOf(item.url)} is here`}
                      className="ref-save-why co-glass-control"
                    />
                  </div>
                  <button type="button" className="ref-save-remove" aria-label="Remove" onClick={() => remove(item.id)}>
                    <X aria-hidden className="size-3.5" />
                  </button>
                </div>
              ) : item.kind === "idea" ? (
                <div key={item.id} className="ref-save-row">
                  <span className="ref-save-thumb ref-save-thumb--idea grid place-items-center" aria-hidden>
                    <Lightbulb className="size-[18px] text-co-text2" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="ref-mono-faint">Idea</span>
                    <textarea
                      rows={1}
                      value={item.text}
                      onChange={(e) => patch(item.id, { text: e.target.value })}
                      aria-label="The idea"
                      className="ref-save-idea"
                    />
                  </div>
                  <button type="button" className="ref-save-remove" aria-label="Remove" onClick={() => remove(item.id)}>
                    <X aria-hidden className="size-3.5" />
                  </button>
                </div>
              ) : (
                <div key={item.id} className="ref-save-row">
                  <span className="ref-save-thumb ref-save-thumb--tall relative overflow-hidden" aria-hidden>
                    {item.file.type.startsWith("video/") ? (
                      <video src={item.preview} muted playsInline preload="metadata" className="size-full object-cover" />
                    ) : (
                      <img src={item.preview} alt="" className="size-full object-cover" />
                    )}
                    {!item.s3Key ? (
                      <>
                        <span className="absolute inset-0 rounded-card bg-[#12121266]" />
                        <span className="absolute bottom-0 left-0 h-[3px] bg-co-accent" style={{ width: `${item.progress}%` }} />
                      </>
                    ) : null}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className={cn("ref-mono-faint truncate", item.error ? "text-co-alert-ink" : !item.s3Key && "text-co-muted")}>
                      {item.file.name} · {sizeLabel(item.file.size)}
                      {item.error ? ` · ${item.error}` : !item.s3Key ? ` · Uploading ${item.progress}%` : ""}
                    </span>
                    <textarea
                      rows={1}
                      value={item.why}
                      onChange={(e) => patch(item.id, { why: e.target.value })}
                      placeholder="Why is it here? Say what to take from it."
                      aria-label={`Why ${item.file.name} is here`}
                      className="ref-save-why co-glass-control"
                    />
                  </div>
                  <button type="button" className="ref-save-remove" aria-label="Remove" onClick={() => remove(item.id)}>
                    <X aria-hidden className="size-3.5" />
                  </button>
                </div>
              ),
            )}

            <button type="button" className="ref-drop" onClick={() => fileInput.current?.click()}>
              <ImagePlus aria-hidden className="size-[15px] text-co-muted" />
              Drop pictures or videos here
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {overCap ? (
              <p className="text-[12.5px] leading-[1.5] text-co-alert-ink">
                Past the ten-item cap. Whatever the queue will not take comes back in the activity line with its reason, so nothing goes missing quietly.
              </p>
            ) : null}
            {tooLong ? (
              <p className="text-[12.5px] leading-[1.5] text-co-alert-ink">
                The links, ideas and whys together run past {MAX_CHARS} characters. Shorten a why or save the rest next.
              </p>
            ) : null}
            {failed ? (
              <p role="alert" className="text-[12.5px] leading-[1.5] text-co-alert-ink">
                Could not save. {failed}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-co-line px-6 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="ref-mono-faint">For</span>
              <ReferenceBrandSelect
                label="Save for"
                brands={brands}
                value={brandId}
                allowEveryone={allowEveryone}
                onChange={setBrandId}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className={cn("ref-mono", overCap ? "text-co-alert-ink" : "text-co-muted")}>
                {overCap ? `${textItems.length} / ${MAX_ITEMS} items` : `${count} ${count === 1 ? "item" : "items"}`}
              </span>
              <button
                type="button"
                className="ref-btn ref-btn--primary h-9 px-4"
                disabled={saving || uploading || brandMissing || tooLong || count === 0}
                aria-busy={saving}
                onClick={() => void save()}
              >
                <Plus aria-hidden className="size-3.5" />
                Save
              </button>
            </div>
          </div>
         </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
